import type { Connection, Import } from "@riea-water/contracts";

/** The entity kinds a person can upload, in the prototype's wording. */
export const ENTITY_KINDS: readonly { value: string; label: string }[] = [
  { value: "supply_points", label: "Supply points" },
  { value: "premises", label: "Premises" },
  { value: "meters", label: "Meters" },
  { value: "reads", label: "Reads" },
  { value: "field_jobs", label: "Field jobs" },
  { value: "evidence", label: "Evidence" },
  { value: "address_base", label: "Address base" },
  { value: "connections", label: "Connections register" },
];

export function entityKindLabel(kind: string): string {
  return ENTITY_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

/** Quarantine reason codes to the words the bars show (packages/importer reasons). */
export const REASON_LABELS: Record<string, string> = {
  missing_required: "Required value missing",
  bad_spid_shape: "SPID not in the expected shape",
  bad_uprn: "UPRN not 12 digits",
  bad_date: "Date not ISO 8601",
  non_numeric_value: "Value not numeric",
  bad_code: "Value not one of the allowed codes",
  unknown_premises_ref: "Unknown premises reference",
  unknown_supply_point: "Unknown supply point",
  unknown_meter_ref: "Unknown meter reference",
  duplicate_row: "Duplicate row",
  wrong_column_count: "Row has the wrong number of columns",
};

export function reasonLabel(code: string): string {
  return REASON_LABELS[code] ?? code.replace(/_/g, " ");
}

/** Reason bars as the prototype draws them: largest first, widths relative to the largest. */
export function reasonBars(
  byReason: Record<string, number>,
): { code: string; label: string; count: number; width: number }[] {
  const entries = Object.entries(byReason)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
  const max = entries[0]?.[1] ?? 1;
  return entries.map(([code, count]) => ({
    code,
    label: reasonLabel(code),
    count,
    width: Math.max(2, Math.round((count / max) * 100)),
  }));
}

/** The connections a file of this kind can arrive through (D-31). */
export function connectionsFor(kind: string, connections: readonly Connection[]): Connection[] {
  return connections.filter(
    (c) =>
      c.buildStatus !== "planned" &&
      c.templates.includes(kind) &&
      (c.connector === "file" || c.connector === "job_object"),
  );
}

export function statusLabel(status: Import["status"]): string {
  switch (status) {
    case "validating":
      return "Validating";
    case "ready_to_commit":
      return "Ready to commit";
    case "committing":
      return "Committing";
    case "committed":
      return "Committed";
    case "failed":
      return "Failed";
  }
}

export const number = (n: number) => n.toLocaleString("en-GB");
